import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ThemedText } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { calculateDaysUntil } from '@/data/trips';

type TripStatus = 'upcoming' | 'in_progress' | 'completed' | 'past';

export function getTripStatus(startDate: Date, endDate: Date): TripStatus {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  const daysUntil = calculateDaysUntil(startDate);
  
  if (now >= start && now <= end) {
    return 'in_progress';
  }
  if (now > end) {
    return 'completed';
  }
  if (daysUntil <= 0) {
    return 'upcoming';
  }
  return 'upcoming';
}

type TripStatusBadgeProps = {
  startDate: Date;
  endDate: Date;
  daysUntil?: number;
  size?: 'small' | 'medium';
  showCompletionMeta?: boolean;
  showLockedIndicator?: boolean;
};

const formatCompletionDate = (date: Date): string =>
  date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

export function TripStatusBadge({
  startDate,
  endDate,
  daysUntil,
  size = 'small',
  showCompletionMeta = false,
  showLockedIndicator = false,
}: TripStatusBadgeProps) {
  const theme = useTheme();
  const status = getTripStatus(startDate, endDate);
  const days = daysUntil ?? calculateDaysUntil(startDate);
  
  const statusConfig = {
    upcoming: {
      label: days > 0 ? `${days}d` : 'Soon',
      fullLabel: days > 0 ? `in ${days} ${days === 1 ? 'day' : 'days'}` : 'Starting soon',
      color: theme.colors.primary,
      bgColor: theme.colors.primary + '15',
      icon: days <= 7 ? 'time' : 'calendar-outline' as keyof typeof Ionicons.glyphMap,
    },
    in_progress: {
      label: 'Now',
      fullLabel: 'In progress',
      color: theme.colors.success,
      bgColor: theme.colors.success + '20',
      icon: 'radio-button-on' as keyof typeof Ionicons.glyphMap,
    },
    completed: {
      label: showCompletionMeta ? formatCompletionDate(endDate) : 'Done',
      fullLabel: `Completed ${formatCompletionDate(endDate)}`,
      color: theme.colors.textSecondary,
      bgColor: theme.colors.borderMuted,
      icon: 'checkmark-circle' as keyof typeof Ionicons.glyphMap,
    },
    past: {
      label: 'Past',
      fullLabel: 'Past',
      color: theme.colors.textSecondary,
      bgColor: theme.colors.borderMuted,
      icon: 'archive-outline' as keyof typeof Ionicons.glyphMap,
    },
  };
  
  const config = statusConfig[status];
  const fontSize = size === 'small' ? 11 : 12;
  const padding = size === 'small' ? theme.spacing(0.5) : theme.spacing(0.75);
  const paddingHorizontal = size === 'small' ? theme.spacing(1.25) : theme.spacing(1.5);
  const iconSize = size === 'small' ? 12 : 14;
  
  const showLock = showLockedIndicator && status === 'completed';

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(0.35),
        backgroundColor: config.bgColor,
        paddingVertical: padding,
        paddingHorizontal: paddingHorizontal,
        borderRadius: theme.radii.md,
        alignSelf: 'flex-start',
      }}
    >
      <Ionicons name={config.icon} size={iconSize} color={config.color} />
      {showLock && (
        <Ionicons name="lock-closed-outline" size={iconSize} color={config.color} />
      )}
      <ThemedText
        variant="label"
        style={{
          fontSize,
          color: config.color,
          fontWeight: '600',
        }}
      >
        {size === 'small' ? config.label : config.fullLabel}
      </ThemedText>
    </View>
  );
}

